// THE active project plan — one precedence, shared by every surface.
//
// Studio used to answer "which project plan is in force?" three different ways:
//
//   * the store, on load:  readStudioProjectPlan(settings)
//                            ?? meta.studioProjectPlan
//                            ?? deriveStudioProjectPlanFromMeta(meta)
//   * publish-surface:     projectPlan ?? projectMeta?.studioProjectPlan
//   * preview-surface:     manifest?.studioProjectPlan ?? projectRecord?.projectPlan
//
// Those agree only while every copy is in sync. They diverge the moment one
// copy moves without the others — which is exactly what a Library binding does:
// the compiled plan lands in the STORE first, so Publish reports the bound
// release while Preview keeps reporting the stale manifest copy, and a creator
// sees two contradictory answers about the same project.
//
// This module is the single answer. The order below IS the store's own
// precedence, extended over the copies the surfaces used to read directly, so:
//   * where the sources agree, every surface renders exactly what it rendered
//     before (the winning value is the same object);
//   * where they disagree, the freshest source — the store — wins everywhere.
//
// Normalization policy is deliberately asymmetric, to keep the "byte-identical
// where sources agree" promise honest: the store plan and `projectMeta`'s plan
// are already typed `StudioProjectPlan`s produced inside Studio, so they pass
// through by IDENTITY. Only the untyped server-payload copies (manifest /
// project record) are normalized through `readStudioProjectPlan`.
//
// Callers that want the raw winning record (report renderers that print
// whatever the producer wrote) read `.record`; callers that want the typed plan
// read `.plan`. Both come from one precedence walk, so they cannot disagree.

import {
  STUDIO_PROJECT_PLAN_SETTINGS_KEY,
  readStudioProjectPlan,
  type StudioProjectPlan,
} from './studio-project-plan'

/** Which copy of the plan won. `null` when no copy exists. */
export type StudioActiveProjectPlanSource =
  | 'store'
  | 'project-meta'
  | 'manifest'
  | 'project-record'

export interface StudioActiveProjectPlanResolution {
  source: StudioActiveProjectPlanSource | null
  /** The winning plan exactly as its producer wrote it. */
  record: Record<string, unknown> | null
  /** The winning plan as the Studio plan contract, when it satisfies it. */
  plan: StudioProjectPlan | null
}

export interface StudioActiveProjectPlanSources {
  /** `useStudioStore(s => s.projectPlan)` — the plan the store loaded. */
  storePlan?: StudioProjectPlan | null
  /** `useStudioStore(s => s.projectMeta)`. */
  projectMeta?: unknown
  /** The app manifest (`currentProject.manifest`). */
  manifest?: unknown
  /** The whole project record (`currentProject`). */
  projectRecord?: unknown
}

const EMPTY: StudioActiveProjectPlanResolution = {
  source: null,
  record: null,
  plan: null,
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** Normalize an untyped server-payload copy; `null` when it is not a plan. */
function normalized(
  source: StudioActiveProjectPlanSource,
  record: Record<string, unknown>,
): StudioActiveProjectPlanResolution {
  return {
    source,
    record,
    plan: readStudioProjectPlan({
      [STUDIO_PROJECT_PLAN_SETTINGS_KEY]: record,
    }),
  }
}

/**
 * Resolve the one plan in force for this project.
 *
 * Pass every source the caller has; omitted sources are simply skipped, so a
 * surface with only the store still gets the same answer as one with all four.
 */
export function resolveStudioActiveProjectPlan(
  sources: StudioActiveProjectPlanSources,
): StudioActiveProjectPlanResolution {
  // 1. The store — the freshest copy, and the only one a just-bound Library
  //    plan reaches before a save round trip.
  const storePlan = sources.storePlan
  if (storePlan) {
    return {
      source: 'store',
      record: storePlan as unknown as Record<string, unknown>,
      plan: storePlan,
    }
  }

  // 2. Project metadata — already a typed plan, so identity, not normalization.
  const metaPlan = asRecord(
    asRecord(sources.projectMeta)?.[STUDIO_PROJECT_PLAN_SETTINGS_KEY],
  )
  if (metaPlan) {
    return {
      source: 'project-meta',
      record: metaPlan,
      plan: metaPlan as unknown as StudioProjectPlan,
    }
  }

  // 3–4. Untyped server payloads.
  const manifestPlan = asRecord(
    asRecord(sources.manifest)?.[STUDIO_PROJECT_PLAN_SETTINGS_KEY],
  )
  if (manifestPlan) return normalized('manifest', manifestPlan)

  const recordPlan = asRecord(asRecord(sources.projectRecord)?.projectPlan)
  if (recordPlan) return normalized('project-record', recordPlan)

  return EMPTY
}
